import { CustomerProjectModelType, projectTypesInversed } from '@teinor/erp/types/company/customer/customerProject'
import { ConnectedProps, connect } from 'react-redux'
import { useParams } from 'react-router'
import { ComponentWithPermissions } from '../../../../baseComponents/CreateRoutering/types'
import TwinTable from '../../../../baseComponents/TwinTable'
import BaseScreenTableInner from '../../../../baseComponents/TwinTable/BaseScreenTableInner'
import { TableComponentBaseScreenInnerProps } from '../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderString, CustomRenderStringAndColorNull, CustomRenderBoolean, EditRow } from '../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import withLoading from '../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../utils/reducers'
import { CustomerProjectTypePayload } from '../../../../utils/reducers/customers/customerProjectTypes'
import ModalCreateCustomerProject from '../../ModalCreateCustomerProject'
import ModalEditCustomerProject from '../../ModalEditCustomerProject'
import { EmployeesPayload } from '../../../../utils/reducers/company/employees'
import { useMemo } from 'react'
import { getAllClientManagerEmployees, getConfigParam } from '../../../../utils/reducers/getters'

interface CustomerProjectsProps extends ComponentWithPermissions {
}

const CustomerProjects: React.FC<CustomerProjectsProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const params = useParams<{ id: string }>()
    const id = params.id
    const pageData = {
        title: t('projects', 'Proyectos'),
        newText: t('addProject', 'Añadir un proyecto'),
    }

    return (
        <BaseScreenTableInner TableComponent={TableCustomerProjectsConnect} extraWhereParams={{ 'CustomerId': id }} pageData={pageData} CreateModalComponent={ModalCreateCustomerProject} EditModalComponent={ModalEditCustomerProject} userPermissions={userPermissions} />
    )
}

type TableCustomerProjectsProps = TableComponentBaseScreenInnerProps & ReduxCustomerProjectTypes & {
    className?: string
    name?: string
    hideLoadFiltersButton?: boolean
}

const TableCustomerProjects: React.FC<TableCustomerProjectsProps> = ({ setTableInstance, extraWhereParams, setOpened, employees, ...rest }) => {
    const { t } = useTwinTranslation()
    const myEmployees = useMemo(() => getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}, [employees])
    const columns: ColumnTableSchema<CustomerProjectModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            width: 75
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'CustomerProject.name'
        },
        {
            id: 'description',
            dataKey: 'description',
            label: t('description', 'Descripción'),
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'CustomerProjectTypeId',
            label: t('projectType', 'Tipo de proyecto'),
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProjectType?.name} />
        },
        {
            id: 'CurrentCProjectStatusId',
            dataKey: 'CurrentCProjectStatusId',
            label: t('status', 'Estado'),
            customRender: (_parameterValue, allRowData) => <CustomRenderStringAndColorNull value={allRowData?.CurrentCProjectStatus?.name} color={allRowData?.CurrentCProjectStatus?.color} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('status', 'Estado') }
            },
            searchKey: 'CurrentCProjectStatus.name',
            sortable: true,
            sortKey: 'CurrentCProjectStatus.order'
        },
        {
            id: 'ResponsibleEmployeeId',
            dataKey: 'Customer',
            label: t('ourManager', 'Nuestro gestor'),
            minWidth: 170,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.ResponsibleEmployee?.fullname_short} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: myEmployees, label: t('ourManager', 'Nuestro gestor'), fieldName: 'fullname_short' },
            },
            sortable: true,
            hideColumnByDefault: true,
            searchSingleKey: 'Customer.ResponsibleEmployee.id',
            searchKey: 'Customer.ResponsibleEmployee.fullname_short'
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'CustomerProject.active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
    ]
    return (
        <TwinTable name='CustomerProjects' columns={columns} defaultOrder={{ order: 'desc', defaultOrderField: 'createdAt' }} getDataFrom='/api/app/customer/customerProject/getAllCustomerProjects'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })}
            setTableInstance={setTableInstance} extraWhereParams={{ ...extraWhereParams, projectType: projectTypesInversed['project'] }} {...rest} />
    )
}

const customerProjectTypesDispatch = {
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxCustomerProjectTypes = ConnectedProps<typeof customerProjectTypesConnect>
const mapCustomerProjectTypesConnector = (state: AllReduxPayloads) => ({ customerProjectTypes: state.customerProjectTypes, employees: state.employees })
const customerProjectTypesConnect = connect(mapCustomerProjectTypesConnector, customerProjectTypesDispatch)

const customerProjectTypesConnectLoading = withLoading(TableCustomerProjects, [{ fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])

export const TableCustomerProjectsConnect = customerProjectTypesConnect(customerProjectTypesConnectLoading)

export default CustomerProjects